import React, { useEffect, useState } from "react";
import { Share2, Copy, CheckCircle2, Trash2, Clock, X } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";

function countdown(iso) {
  const s = Math.max(0, Math.floor((new Date(iso) - Date.now()) / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function ShareSheet({ deviceId, open, onClose }) {
  const { t } = useAuth();
  const [links, setLinks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState("");
  const [, force] = useState(0);

  const load = async () => {
    if (!deviceId) return;
    try {
      const { data } = await api.get(`/share/by-device/${deviceId}`);
      setLinks(data);
    } catch (_) {}
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, deviceId]);
  useEffect(() => {
    if (!open) return;
    const iv = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(iv);
  }, [open]);

  const create = async (minutes) => {
    setCreating(true);
    try {
      await api.post("/share", { device_id: deviceId, minutes });
      await load();
    } finally { setCreating(false); }
  };
  const revoke = async (token) => {
    await api.delete(`/share/${token}`);
    await load();
  };
  const copy = async (token) => {
    const url = `${window.location.origin}/s/${token}`;
    try { await navigator.clipboard.writeText(url); } catch (_) {}
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(""), 1400);
  };

  if (!open) return null;

  return (
    <div
      data-testid="share-sheet"
      className="fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center animate-fadeIn"
      onClick={onClose}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-2xl p-5 shadow-soft"
        style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom))" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-light text-primary flex items-center justify-center">
              <Share2 size={18} />
            </div>
            <div>
              <h2 className="font-black text-lg text-slate-900">{t.shareLive}</h2>
              <p className="text-xs text-slate-500 mt-0.5 max-w-xs">{t.shareDesc}</p>
            </div>
          </div>
          <button
            data-testid="share-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            data-testid="share-create-15"
            disabled={creating}
            onClick={() => create(15)}
            className="py-2.5 rounded-xl bg-primary text-white font-bold text-sm active:scale-[0.98] disabled:opacity-60"
          >
            {t.create15Min}
          </button>
          <button
            data-testid="share-create-30"
            disabled={creating}
            onClick={() => create(30)}
            className="py-2.5 rounded-xl bg-primary-light text-primary font-bold text-sm active:scale-[0.98] disabled:opacity-60"
          >
            {t.create30Min}
          </button>
        </div>

        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
            {t.activeLinks}
          </div>
          {links.length === 0 ? (
            <div data-testid="share-empty" className="text-center text-sm text-slate-400 py-4">
              {t.noActiveLinks}
            </div>
          ) : (
            <ul data-testid="share-links" className="space-y-2 max-h-[40vh] overflow-auto no-scrollbar">
              {links.map((l) => {
                const url = `${window.location.origin}/s/${l.token}`;
                const expired = new Date(l.expires_at) <= new Date();
                return (
                  <li
                    key={l.token}
                    data-testid={`share-link-${l.token}`}
                    className="border border-slate-200 rounded-xl p-3 flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-slate-700 truncate">/s/{l.token}</div>
                      <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5">
                        <Clock size={11} />
                        {t.expiresIn} {expired ? "—" : countdown(l.expires_at)}
                      </div>
                    </div>
                    <button
                      data-testid={`share-copy-${l.token}`}
                      onClick={() => copy(l.token)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary text-white text-xs font-bold"
                      title={url}
                    >
                      {copiedToken === l.token ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                      {copiedToken === l.token ? t.linkCopied : t.copyLink}
                    </button>
                    <button
                      data-testid={`share-revoke-${l.token}`}
                      onClick={() => revoke(l.token)}
                      className="text-parked p-1.5 rounded-lg hover:bg-red-50"
                      title={t.revoke}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
